<div class="bg-gray-50 min-h-screen p-2">
  <div class="max-w-4xl mx-auto">
    <div class="bg-white rounded-lg shadow-sm relative">
      <!-- Loading Overlay for Edit Mode -->
      <div *ngIf="loadingProduct && isEditMode" class="absolute inset-0 bg-white bg-opacity-95 flex flex-col items-center justify-center z-50 rounded-lg">
        <app-loading-spinner size="large" type="inline"></app-loading-spinner>
        <p class="text-gray-600 mt-4 text-sm" i18n>Loading product details...</p>
      </div>

      <div class="px-6 py-4 border-b border-gray-200">
        <h2 class="text-xl font-semibold text-gray-900">{{ getPageTitle() }}</h2>
        <p class="text-gray-600 mt-1" i18n>
          {{ isEditMode ? 'Update product information' : 'Create a new product for your company' }}
        </p>
      </div>

      <form [formGroup]="productForm" (ngSubmit)="onSubmit()" class="p-6 space-y-6" [class.opacity-50]="loadingProduct && isEditMode" [class.pointer-events-none]="loadingProduct && isEditMode">
        <!-- Product Name Field -->
        <div>
          <label for="name" class="block text-sm font-medium text-gray-700 mb-2" i18n>
            Product / Service Name <span class="text-red-500">*</span>
          </label>
          <input 
            pInputText 
            id="name"
            type="text" 
            formControlName="name"
            placeholder="Enter the product / service name" i18n-placeholder
            [class.border-red-500]="isFieldInvalid('name')"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <div class="text-red-500 text-sm mt-1" *ngIf="isFieldInvalid('name')">
            {{ getFieldError('name') }}
          </div>
          <p class="text-gray-500 text-sm mt-1" i18n>
            Enter a descriptive name for your product (2-100 characters)
          </p>
        </div>


        <!-- Price Fields -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label for="minPrice" class="block text-sm font-medium text-gray-700 mb-2" i18n>
              Min-Price
            </label>
            <input 
              pInputText 
              id="minPrice"
              type="number" 
              formControlName="minPrice"
              placeholder="Enter the product / service cost" i18n-placeholder
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <div class="text-red-500 text-sm mt-1" *ngIf="isFieldInvalid('minPrice')">
              {{ getFieldError('minPrice') }}
            </div>
          </div>

          <div>
            <label for="maxPrice" class="block text-sm font-medium text-gray-700 mb-2" i18n>
              Max-Price
            </label>
            <input 
              pInputText 
              id="maxPrice"
              type="number" 
              formControlName="maxPrice"
              placeholder="Enter the product / service cost" i18n-placeholder
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <div class="text-red-500 text-sm mt-1" *ngIf="isFieldInvalid('maxPrice')">
              {{ getFieldError('maxPrice') }}
            </div>
          </div>
        </div>

        <!-- Currency Field -->
        <div>
          <label for="currency" class="block text-sm font-medium text-gray-700 mb-2" i18n>
            Currency <span class="text-red-500">*</span>
          </label>
          <p-select 
            id="currency"
            [options]="currencyOptions" 
            formControlName="currency"
            placeholder="Select Currency" i18n-placeholder
            styleClass="w-full"
          ></p-select>
          <div class="text-red-500 text-sm mt-1" *ngIf="isFieldInvalid('currency')">
            {{ getFieldError('currency') }}
          </div>
        </div>

        <!-- Single Price Field -->
        <div>
          <label for="price" class="block text-sm font-medium text-gray-700 mb-2" i18n>
            Price (Alternative)
          </label>
          <input 
            pInputText 
            id="price"
            type="text" 
            formControlName="price"
            placeholder="e.g., 12,000 EUR" i18n-placeholder
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <p class="text-gray-500 text-sm mt-1" i18n>
            Enter a single price or price range (optional if using min/max price)
          </p>
        </div>

        <!-- Description Field -->
        <div>
          <label for="description" class="block text-sm font-medium text-gray-700 mb-2" i18n>
            Description <span class="text-red-500">*</span>
          </label>
          <p-editor 
            id="description"
            formControlName="description"
            placeholder="Enter your detailed product description with rich formatting..." i18n-placeholder
            [style]="getEditorConfig().style"
            [formats]="getEditorConfig().formats"
            [modules]="getEditorConfig().modules"
            [class.border-red-500]="isFieldInvalid('description')"
          >
            <ng-template #header>
              <span class="ql-formats">
                <button type="button" class="ql-bold" aria-label="Bold" i18n-aria-label></button>
                <button type="button" class="ql-italic" aria-label="Italic" i18n-aria-label></button>
                <button type="button" class="ql-underline" aria-label="Underline" i18n-aria-label></button>
              </span>
              <span class="ql-formats">
                <button type="button" class="ql-list" value="ordered" aria-label="Ordered List" i18n-aria-label></button>
                <button type="button" class="ql-list" value="bullet" aria-label="Bullet List" i18n-aria-label></button>
              </span>
              <span class="ql-formats">
                <button type="button" class="ql-link" aria-label="Link" i18n-aria-label></button>
              </span>
            </ng-template>
          </p-editor>
          <div class="text-red-500 text-sm mt-1" *ngIf="isFieldInvalid('description')">
            {{ getFieldError('description') }}
          </div>
          <p class="text-gray-500 text-sm mt-1" i18n>
            Provide additional details about your product with rich formatting
          </p>
        </div>

        <!-- Available Territories Section -->
        <div class="bg-gray-50 rounded-lg p-4">
          <h3 class="text-lg font-medium text-gray-900 mb-4" i18n>Select Available Territories</h3>
          
          <div *ngIf="loadingTerritories" class="text-center py-4">
            <i class="pi pi-spin pi-spinner text-2xl text-blue-500"></i>
            <p class="text-gray-500 mt-2" i18n>Loading territories...</p>
          </div>

          <div *ngIf="!loadingTerritories && getRegionKeys().length === 0" class="text-center py-4">
            <i class="pi pi-map text-2xl text-gray-400"></i>
            <p class="text-gray-500 mt-2" i18n>No territories available</p>
          </div>

            <p-accordion *ngIf="!loadingTerritories && getRegionKeys().length > 0" [value]="getExpandedPanels()" [multiple]="true">
              <p-accordion-panel 
                *ngFor="let region of getRegionKeys(); let i = index" 
                [value]="i"
              >
                <p-accordion-header>{{ region }}</p-accordion-header>
                <p-accordion-content>
                  <div class="space-y-3">
                    <div 
                      *ngFor="let territory of getTerritoriesByRegion(region)" 
                      class="flex items-center"
                    >
                                         <input 
                       type="checkbox"
                       [id]="'territory-' + territory._id"
                       [checked]="isTerritorySelected(territory)"
                       class="mr-3 w-4 h-4 text-blue-600 bg-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                       (change)="onTerritoryCheckboxChange(territory, $event)"
                     />
                      <label 
                        [for]="'territory-' + territory._id" 
                        class="text-gray-900 cursor-pointer flex-1"
                      >
                        {{ territory.name }}
                      </label>
                      <span 
                        *ngIf="territory.status === 'Active'" 
                        class="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full"
                      >
                        Active
                      </span>
                      <span 
                        *ngIf="territory.status !== 'Active'" 
                        class="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full"
                      >
                        Inactive
                      </span>
                    </div>
                  </div>
                </p-accordion-content>
              </p-accordion-panel>
            </p-accordion>

          <div *ngIf="selectedTerritories.length > 0" class="mt-4 p-3 bg-blue-50 rounded-lg">
            <p class="text-sm text-blue-800">
              <i class="pi pi-check-circle mr-2"></i>
              {{ selectedTerritories.length }} territor{{ selectedTerritories.length === 1 ? 'y' : 'ies' }} selected
            </p>
          </div>
        </div>

        <!-- Active Status Field -->
        <div *ngIf="isEditMode">
          <label class="flex items-center">
            <p-toggleSwitch 
              formControlName="isActive"
              class="mr-3"
            ></p-toggleSwitch>
            <span class="text-sm font-medium text-gray-700" i18n>Active product</span>
          </label>
          <p class="text-gray-500 text-sm mt-1" i18n>
            Toggle to activate or deactivate this product
          </p>
        </div>

        <!-- Form Actions -->
        <div class="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button 
            pButton 
            type="button" 
            label="Cancel" i18n-label
            icon="pi pi-times" 
            class="p-button-secondary"
            (click)="onCancel()"
            [disabled]="loading"
          ></button>
          
          <button 
            pButton 
            type="submit" 
            [label]="getSubmitButtonText()" i18n-label  
            icon="pi pi-check" 
            class="p-button-primary"
            [loading]="loading"
            [disabled]="productForm.invalid || loading"
          ></button>
        </div>
      </form>
    </div>
  </div>

  <!-- Toast Messages -->
  <p-toast></p-toast>
</div>
